'use client'

import React, { useEffect, useState } from 'react'
import {
  Star,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ArrowUpRight,
  SlidersHorizontal,
} from 'lucide-react'
import { cn } from '@/utilities/ui'
import Link from 'next/link'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Agency } from '@/payload-types'
import { useRouter } from 'next/navigation'
import { useDebounce } from '@/utilities/useDebounce'
import Pagination from '@/components/Pagination/Pagination'
import { useTheme } from '@/providers/Theme'
import Image from 'next/image'

type Props = {
  className?: string
  dictionary?: any
  lang: string
  isPromoter?: boolean
  Agencies: {
    docs: AgencyWithCount[]
    hasNextPage: boolean
    hasPrevPage: boolean
    limit: number
    nextPage: number | null
    page: number
    pagingCounter: number
    prevPage: number | null
    totalDocs: number
    totalPages: number
  }
  query: string
}

interface AgencyWithCount extends Agency {
  propertiesCount?: number
  media?: {
    filename: string
  };
}

const AgencyCard = ({
  agency,
  dictionary,
  lang,
}: {
  agency: AgencyWithCount
  dictionary?: any
  lang: string
}) => {
  const { theme } = useTheme()
  return (
    <Link
      href={`/${lang}/agencies/${agency.slug}`}
      className={cn("relative group bg-card rounded-lg shadow-sm overflow-hidden hover:[box-shadow:0px_10px_40px_0px_rgba(24,_26,_32,_0.05)] transition-all duration-300 cursor-pointer h-[434px] px-[30px] py-5 flex flex-col justify-between gap-[30px] border", theme === 'dark' ? 'border-card' : '')}
    >
     
      {/* Agency Image */}
      <div className="relative w-full h-full z-10">
        {agency?.media?.filename ? (
          <Image
            src={'/api/media/file/'+agency.media?.filename}
            alt={agency.title || 'logo'}
            className="w-full h-full object-contain"
            fill
            priority
          />
        ) : (
          <div className="w-full h-[200px] bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500">No image</span>
          </div>
        )}
      </div>

      {/* Agency Info */}
      <div className="flex flex-col gap-3">
        {/* Rating */}
        <div className="flex items-center">
          {/* Properties count */}
          {agency?.propertiesCount && (
            <div className="rounded-md bg-primary-300 text-white text-[12px] font-semibold px-2 py-1 z-20">
              {agency?.propertiesCount} {dictionary?.agencyList?.properties || 'Properties'}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {/* Agency Name */}
          <h3 className="text-typography text-[15px] font-semibold line-clamp-1">
            {agency?.title}
          </h3>

          {/* Address */}
          <p className="text-typography text-[15px] mb-2 line-clamp-1">
            {agency.agencyContactSection?.address}
          </p>

          {/* View Listings Button */}
          <div
            className={cn(
              `h-[50px] flex items-center justify-center gap-2 w-full rounded-md border border-border transition-colors duration-300 text-black
              ${theme === 'dark' ? 'group-hover:!bg-primary group-hover:bg-card !text-white' : 'group-hover:!bg-primary group-hover:text-white bg-card'}`,
            )}
          >
            {dictionary?.agencyList?.viewListings || 'View Listings'} <ArrowUpRight size={16} />
          </div>
        </div>
      </div>
    </Link>
  )
}

export const AgenciesList = ({ className, Agencies, query, dictionary, lang, isPromoter }: Props) => {
  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState('')
  const [category, setCategory] = useState('all')
  const [city, setCity] = useState('all')
  const [sortBy, setSortBy] = useState('newest')

  const params = new URLSearchParams()
  const { page, totalPages, hasNextPage, hasPrevPage } = Agencies

  const createPageLink = (pageNumber: number) => {
    if (query) params.set('q', query)
    params.set('page', pageNumber.toString())
    router.push(`/${lang}/agencies?${params.toString()}`)
  }

  const [value, setValue] = useState('')

  return (
    <div className={cn('py-8', className)}>
      <div className="container mx-auto px-4">
        <div className="flex items-start justify-between">
          {/* Header */}
          <div className="mb-6 space-y-4">
            <h1 className="text-typography text-[20px] sm:text-2xl md:text-[33px] font-extrabold">
              {!isPromoter 
                ? dictionary?.agencyList?.agencies || 'Agencies' 
                : dictionary?.agencyList?.developers || 'Real Estate Developers'}
            </h1>
          </div>

          <div className="text-typography text-center mt-4 text-sm">
            {Agencies?.page} – {Agencies?.limit} {dictionary?.agencyList?.of || 'of'}{' '}
            {Agencies?.totalDocs}+{' '}
            {!isPromoter 
              ? dictionary?.agencyList?.propertyAvailable || 'Agency available' 
              : dictionary?.agencyList?.developersAvailable || 'Developers available'}
          </div>

          {/* Mobile Filter Button */}
          <Button
            type="button"
            variant={'ghost'}
            className="hidden w-[97px] h-[40px] text-sm items-center justify-center bg-white rounded-2xl [box-shadow:0px_1px_4px_0px_rgba(24,26,32,0.07)] md:hidden"
          >
            <SlidersHorizontal className="mr-2 h-5 w-5" />
            {dictionary?.agencyList?.filter || 'Filter'}
          </Button>
        </div>

        {/* Filter Bar - hidden */}
        <div className="hidden flex-row max-md:justify-between gap-4 mb-8">
          {/* Hidden content */}
        </div>

        {/* Agency Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {Agencies?.docs?.map((agency) => (
            <AgencyCard key={agency.id} agency={agency} dictionary={dictionary} lang={lang} />
          ))}
        </div>

        {/* Pagination */}
        {Agencies?.totalDocs > Agencies?.limit && (
          <>
            <Pagination
              count={totalPages}
              page={page}
              pagesize={1}
              setPage={createPageLink}
              showPages={3}
              hasPrevPage={hasPrevPage}
              hasNextPage={hasNextPage}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default AgenciesList
